/**
 * Sorts an array of objects with a `name` property alphabetically by last name.
 * "Last name" is defined as the last word in the full name string.
 * If last names are identical, it falls back to comparing first names.
 *
 * @param {Array} items - Array of objects with a `name` property (e.g., students).
 * @returns {Array} A new sorted array (does not mutate the original).
 */
export function sortByLastName(items) {
  return [...items].sort((a, b) => {
    const aName = (a.name || [a.firstName, a.lastName].filter(Boolean).join(' ')).trim();
    const bName = (b.name || [b.firstName, b.lastName].filter(Boolean).join(' ')).trim();

    const aParts = aName.split(/\s+/);
    const bParts = bName.split(/\s+/);

    const aLast = (aParts[aParts.length - 1] || '').toLowerCase();
    const bLast = (bParts[bParts.length - 1] || '').toLowerCase();

    const lastCompare = aLast.localeCompare(bLast);
    if (lastCompare !== 0) return lastCompare;

    // Tie-break by first name
    const aFirst = (aParts[0] || '').toLowerCase();
    const bFirst = (bParts[0] || '').toLowerCase();
    return aFirst.localeCompare(bFirst);
  });
}

/**
 * Comparator function for sorting by last name.
 * Use directly in Array.sort() when the items have a `name` property.
 */
export function lastNameComparator(a, b) {
  const aName = (a.name || a.studentName || [a.firstName, a.lastName].filter(Boolean).join(' ')).trim();
  const bName = (b.name || b.studentName || [b.firstName, b.lastName].filter(Boolean).join(' ')).trim();

  const aParts = aName.split(/\s+/);
  const bParts = bName.split(/\s+/);

  const aLast = (aParts[aParts.length - 1] || '').toLowerCase();
  const bLast = (bParts[bParts.length - 1] || '').toLowerCase();

  const lastCompare = aLast.localeCompare(bLast);
  if (lastCompare !== 0) return lastCompare;

  const aFirst = (aParts[0] || '').toLowerCase();
  const bFirst = (bParts[0] || '').toLowerCase();
  return aFirst.localeCompare(bFirst);
}
